import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { QueuedSalesDialog } from '@/components/QueuedSales';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { ShoppingBag, Receipt, Users, Truck, BarChart3, Wallet,
  Settings as SettingsIcon, ShieldCheck, ScrollText, LogOut, CalendarCheck, Database, Trash2,
  Layers, Gauge, ExternalLink, Activity, UserCog, CloudUpload, ArrowLeftRight,
  Package, SlidersHorizontal, Undo2, AlarmClock, Landmark, TrendingUp } from 'lucide-react';

const Item = ({ icon: Icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 text-left active:scale-[0.99] transition-transform">
    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary"><Icon className="h-5 w-5" /></span>
    <span className="text-sm font-medium">{label}</span>
  </button>
);

const GroupTitle = ({ children }) => (
  <p className="px-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>
);

export default function More() {
  const navigate = useNavigate();
  const { user, logout, can } = useApp();
  const isOwner = user?.role === 'OWNER' || user?.owner_access;
  const managerUp = isOwner || user?.role === 'MANAGER';
  const c = (p) => !!(can && can(p));
  const [queuedOpen, setQueuedOpen] = useState(false);

  const seedDemo = async () => { try { await api.post('/admin/seed-demo'); toast.success('Demo data seeded'); } catch (e) { toast.error(errMsg(e)); } };
  const clearDemo = async () => { if (!window.confirm('Remove all demo data?')) return; try { await api.delete('/admin/demo-data'); toast.success('Demo data cleared'); } catch (e) { toast.error(errMsg(e)); } };

  // Mirror the desktop sidebar so mobile has full parity for owners/admins.
  const general = [
    c('stock_view') && { icon: Package, label: 'Items / Stock', testid: 'more-stock', to: '/stock' },
    (c('stock_edit') || c('stock_view') || isOwner) && { icon: SlidersHorizontal, label: 'Stock Adjustment', testid: 'more-stock-adjustment', to: '/stock-adjustment' },
    c('transfer_view') && { icon: ArrowLeftRight, label: 'Transfers', testid: 'more-transfers', to: '/transfers' },
    (c('returns') || c('exchanges')) && { icon: Undo2, label: 'Return / Exchange', testid: 'more-returns', to: '/returns' },
    c('customers_view') && { icon: Users, label: 'Customers', testid: 'more-customers', to: '/customers' },
    c('billing_view') && { icon: Receipt, label: 'Sales Invoices', testid: 'more-sales', to: '/sales' },
    managerUp && { icon: AlarmClock, label: 'Due & Reminders', testid: 'more-due-reminders', to: '/due-reminders' },
    (isOwner || c('reports_view')) && { icon: Landmark, label: 'Cash & Bank', testid: 'more-accounts', to: '/accounts' },
  ].filter(Boolean);

  const accounting = [
    c('purchases_view') && { icon: ShoppingBag, label: 'Purchases', testid: 'more-purchases', to: '/purchases' },
    c('suppliers_view') && { icon: Truck, label: 'Suppliers', testid: 'more-suppliers', to: '/suppliers' },
    c('expenses_view') && { icon: Wallet, label: 'Expenses', testid: 'more-expenses', to: '/expenses' },
    isOwner && { icon: BarChart3, label: 'Reports', testid: 'more-reports', to: '/reports' },
    isOwner && { icon: TrendingUp, label: 'Profit', testid: 'more-profit', to: '/profit' },
    isOwner && { icon: Layers, label: 'Stock Report', testid: 'more-stock-report', to: '/stock-report' },
    isOwner && { icon: CalendarCheck, label: 'Daily Closing', testid: 'more-closing', to: '/daily-closing' },
    isOwner && { icon: ScrollText, label: 'Audit Log', testid: 'more-audit', to: '/audit' },
  ].filter(Boolean);

  const system = [
    (c('users_manage') || isOwner) && { icon: ShieldCheck, label: 'Users & Access', testid: 'more-admin', to: '/admin' },
    managerUp && { icon: UserCog, label: 'Staff & Incentives', testid: 'more-staff', to: '/staff' },
    isOwner && { icon: Database, label: 'Data Management', testid: 'more-data-management', to: '/data-management' },
    isOwner && { icon: Activity, label: 'System Health', testid: 'more-system-health', to: '/system-health' },
    (c('settings_view') || isOwner) && { icon: SettingsIcon, label: 'Settings', testid: 'more-settings', to: '/settings' },
  ].filter(Boolean);

  const Grid = ({ items }) => (
    <div className="grid grid-cols-2 gap-3">
      {items.map((it) => (
        <Item key={it.testid} icon={it.icon} label={it.label} testid={it.testid} onClick={() => navigate(it.to)} />
      ))}
    </div>
  );

  return (
    <Page>
      <TopBar title="More" showStore={false} />
      <div className="px-4 pt-4 space-y-4">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-sm font-semibold">{user?.name}</p>
          <p className="text-xs text-muted-foreground">{user?.username} · {user?.role}</p>
        </div>
        {managerUp && (
          <button data-testid="more-management-dashboard" onClick={() => navigate('/management')}
            className="flex w-full items-center gap-3 rounded-2xl bg-primary px-4 py-4 text-left font-semibold text-primary-foreground transition-transform active:scale-[0.99]">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20"><Gauge className="h-5 w-5" /></span>
            <span className="flex-1 text-sm">Management Dashboard</span>
            <ExternalLink className="h-4 w-4 opacity-80" />
          </button>
        )}

        {general.length > 0 && (<><GroupTitle>General</GroupTitle><Grid items={general} /></>)}
        {accounting.length > 0 && (<><GroupTitle>Accounting</GroupTitle><Grid items={accounting} /></>)}

        <GroupTitle>System</GroupTitle>
        <div className="grid grid-cols-2 gap-3">
          {system.map((it) => (
            <Item key={it.testid} icon={it.icon} label={it.label} testid={it.testid} onClick={() => navigate(it.to)} />
          ))}
          <Item icon={CloudUpload} label="Queued Sales" testid="more-queued-sales" onClick={() => setQueuedOpen(true)} />
        </div>

        {isOwner && (
          <div className="rounded-2xl border border-border bg-card p-4">
            <p className="mb-2 text-sm font-semibold">Demo data</p>
            <div className="flex gap-2">
              <Button variant="secondary" className="h-10 flex-1 rounded-xl" onClick={seedDemo} data-testid="more-seed-demo"><Database className="mr-1 h-4 w-4" />Seed demo</Button>
              <Button variant="secondary" className="h-10 flex-1 rounded-xl text-destructive" onClick={clearDemo} data-testid="more-clear-demo"><Trash2 className="mr-1 h-4 w-4" />Clear demo</Button>
            </div>
          </div>
        )}
        <Button variant="secondary" className="h-12 w-full rounded-xl text-destructive" onClick={logout} data-testid="more-logout"><LogOut className="mr-2 h-4 w-4" />Sign out</Button>
      </div>
      <QueuedSalesDialog open={queuedOpen} onOpenChange={setQueuedOpen} />
    </Page>
  );
}
